import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { requestText } from "../../../shared/requests";

type ViewUrlQrCodeProps = {
  fileName: string;
  url: string;
};

export type ViewUrlQrCodeHandle = {
  exportSvg: () => Promise<boolean>;
};

type PlainQrCodeProps = {
  className?: string;
  size?: number;
  value: string;
};

const QR_CARD_HEIGHT = 369;
const QR_CARD_WIDTH = 320;
const LOGO_MARKUP_FILL = "#ffffff";
const ADMIN_QR_LOGO_URL = "/a/res/assets/intelligo-logo-colorless.svg";

const loadInlineLogoMarkup = async () => {
  const { payload: rawLogoMarkup } = await requestText(ADMIN_QR_LOGO_URL, { handleUnauthorized: false });
  const sanitizedMarkup = rawLogoMarkup
    .replace(/var\(--primary-color,\s*#000000\)/g, LOGO_MARKUP_FILL)
    .replace(/<\?xml[\s\S]*?\?>/g, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .trim();

  const parser = new DOMParser();
  const svgDocument = parser.parseFromString(sanitizedMarkup, "image/svg+xml");
  const svgElement = svgDocument.documentElement;
  const viewBox = svgElement.getAttribute("viewBox") ?? "0 0 100.74118 27.99984";
  const innerMarkup = svgElement.innerHTML;

  return `<svg x="54" y="312" width="212" height="38" viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet">${innerMarkup}</svg>`;
};

const createSvgBlob = (svgMarkup: string) => {
  return new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
};

const saveSvgFile = async (svgMarkup: string, fileName: string) => {
  const fileBlob = createSvgBlob(svgMarkup);
  const windowWithFilePicker = window as Window & {
    showSaveFilePicker?: (options?: {
      suggestedName?: string;
      types?: Array<{
        accept: Record<string, string[]>;
        description: string;
      }>;
    }) => Promise<{
      createWritable: () => Promise<{
        close: () => Promise<void>;
        write: (data: Blob) => Promise<void>;
      }>;
    }>;
  };

  if (windowWithFilePicker.showSaveFilePicker) {
    const fileHandle = await windowWithFilePicker.showSaveFilePicker({
      suggestedName: fileName,
      types: [
        {
          accept: { "image/svg+xml": [".svg"] },
          description: "SVG image",
        },
      ],
    });
    const writable = await fileHandle.createWritable();
    await writable.write(fileBlob);
    await writable.close();
    return;
  }

  const objectUrl = URL.createObjectURL(fileBlob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(objectUrl);
};

export const PlainQrCode = ({ className = "", size = 248, value }: PlainQrCodeProps) => (
  <QRCodeSVG
    bgColor="#ffffff"
    className={className}
    fgColor="#22252b"
    height={size}
    level="H"
    marginSize={1}
    value={value}
    width={size}
  />
);

export const ViewUrlQrCode = forwardRef<ViewUrlQrCodeHandle, ViewUrlQrCodeProps>(({
  fileName,
  url,
}, ref) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [logoMarkup, setLogoMarkup] = useState<string>("");

  useEffect(() => {
    let isCancelled = false;

    void loadInlineLogoMarkup()
      .then((nextLogoMarkup) => {
        if (!isCancelled) {
          setLogoMarkup(nextLogoMarkup);
        }
      })
      .catch((error) => {
        console.error("Failed to load QR logo", error);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleExportSvg = async () => {
    try {
      const svgElement = svgRef.current;
      if (!svgElement) {
        return false;
      }

      const clone = svgElement.cloneNode(true) as SVGSVGElement;
      const svgMarkup = new XMLSerializer().serializeToString(clone);
      await saveSvgFile(svgMarkup, fileName);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return false;
      }
      console.error("Failed to export QR SVG", error);
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    exportSvg: handleExportSvg,
  }), [fileName, url, logoMarkup]);

  return (
    <div className="admin-qr-code-shell">
      <div className="admin-qr-code-panel">
        <svg
          className="admin-qr-code-card"
          height={QR_CARD_HEIGHT}
          ref={svgRef}
          viewBox={`0 0 ${QR_CARD_WIDTH} ${QR_CARD_HEIGHT}`}
          width={QR_CARD_WIDTH}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            fill="#111319"
            height={QR_CARD_HEIGHT}
            rx="40"
            width={QR_CARD_WIDTH}
          />
          <rect
            fill="#ffffff"
            height="276"
            rx="22"
            width="276"
            x="22"
            y="21"
          />
          <QRCodeSVG
            bgColor="#ffffff"
            fgColor="#22252b"
            height={248}
            level="H"
            marginSize={1}
            value={url}
            width={248}
            x={36}
            y={35}
          />
          {logoMarkup ? (
            <g dangerouslySetInnerHTML={{ __html: logoMarkup }} />
          ) : null}
        </svg>
      </div>
    </div>
  );
});

ViewUrlQrCode.displayName = "ViewUrlQrCode";
